import { useEffect } from "react";
import { useMap } from "react-leaflet";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import parseGeoraster from "georaster";
import { SoilRanges } from "./SoilRanges";
import L from "leaflet";
import { SoilThresholds } from "./SoilThresholds";
import { getSoilColor } from "./SoilThresholds";
import "../lib/proj4-setup";
function getColorForValue(value: number, min: number, max: number): string {
  if (isNaN(value)) return "#00000000"; // transparent
  const ratio = (value - min) / (max - min);
  const clamped = Math.max(0, Math.min(1, ratio));
  if (clamped < 0.25) return "#2c7bb6";
  if (clamped < 0.5) return "#abd9e9";
  if (clamped < 0.75) return "#fdae61";
  return "#d7191c";
}

function RasterLayer({
  file,
  property,
  rasterRef,
  legendRef,
  onRasterLoad, 
}: {
  file: string;
  property: string;
  rasterRef: React.MutableRefObject<any>;
  legendRef?: React.MutableRefObject<L.Control | null>;
  onRasterLoad?: (georaster: any) => void;
}) {
  const map = useMap();

  useEffect(() => {
    if (!file || !map) return;
    console.log("Loading raster file:", file);

    

    let layer: any = null;
    let legend: L.Control | null = null;
    let cancelled = false;

    const load = async () => {
      try {
        // Remove previous raster if exists
        if (rasterRef.current) {
          map.removeLayer(rasterRef.current);
          rasterRef.current = null;
        }
        if (legendRef?.current) {
          map.removeControl(legendRef.current);
          legendRef.current = null;
        }
        
        const path = file.startsWith("rasters/") ? file : `rasters/${file}`;
        const response = await fetch(path);
        console.log("📂 Fetching raster:", file, "status:", response.status);
        const arrayBuffer = await response.arrayBuffer();
        console.log("📦 ArrayBuffer length:", arrayBuffer.byteLength);
        const georaster = await parseGeoraster(arrayBuffer);
        console.log("✅ Parsed georaster:", georaster);
        console.log("🟢 Dimensions:", georaster.width, "x", georaster.height);
        console.log("🗺️ Projection:", georaster.projection);

        rasterRef.current = { layer, georaster };
        if (onRasterLoad) {
  console.log("Raster loaded, calling onRasterLoad"); // <--- add this
  onRasterLoad(georaster);
}
        const [min, max] = SoilRanges[property] || [0, 100];

       layer = new GeoRasterLayer({
  georaster,
  opacity: 0.7,
  pixelValuesToColorFn: (values: (number | null)[]) => {
  const val = values[0];
  if (val == null) return "#00000000";
  return getSoilColor(property, val);
},

  resolution: 250,
});

        if (!cancelled) {
          layer.addTo(map);
          rasterRef.current = layer;

          // ---- Add legend ----
          if (legendRef) {
            const LegendControl = L.Control.extend({
              options: { position: "bottomright" },
              onAdd: function () {
                const div = L.DomUtil.create("div", "info legend");
                const grades = [0, 0.25, 0.5, 0.75, 1];
                const labels: string[] = [];

                grades.forEach((g, i) => {
                  const color = getColorForValue(min + g * (max - min), min, max);
                  const text =
                    i === 0 ? `${min}` : i === grades.length - 1 ? `${max}` : "";
                  labels.push(
                    `<i style="background:${color}; width:18px; height:18px; display:inline-block; margin-right:5px"></i>${text}`
                  );
                });

                div.innerHTML = labels.join("<br>");
                return div;
              },
            });

            legend = new LegendControl();
            legend.addTo(map);
            legendRef.current = legend;
          }
        }
      } catch (err) {
        console.error("Error loading raster:", err);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (layer && map) map.removeLayer(layer);
      if (rasterRef.current) rasterRef.current = null;
      if (legend && map) map.removeControl(legend);
      if (legendRef) legendRef.current = null;
    };
  }, [file, property, map, rasterRef, legendRef]);

  return null;
}

export default RasterLayer;
